import { useState, useMemo, useCallback } from "react";
import { isValidPhoneNumber } from "libphonenumber-js/min";
import { newSalespersonContact } from "../../../../../../../models";
import type { Contact } from "../../../../../../../models";
import { useGrowerAssignment, type EntitySortOption } from "../shared";
import type { SalespersonAssignmentProps } from "./SalespersonAssignment.types";

export function useSalespersonAssignmentViewModel(props: SalespersonAssignmentProps) {
	const { jobs, salespersons, salespersonAssignments, onAssignGrowerToSalesperson, onAddSalesperson } = props;

	// New salesperson creation state
	const [isAddingNew, setIsAddingNew] = useState(false);
	const [newName, setNewName] = useState("");
	const [newPhone, setNewPhone] = useState("");
	const [phoneError, setPhoneError] = useState("");

	// Edit state for temp salespersons
	const [editingSpId, setEditingSpId] = useState<string | null>(null);
	const [editName, setEditName] = useState("");
	const [editPhone, setEditPhone] = useState("");
	const [editPhoneError, setEditPhoneError] = useState("");

	// Locally created salespersons (temp IDs)
	const [localSalespersons, setLocalSalespersons] = useState<Contact[]>([]);

	// Merge prop salespersons with locally created ones
	const allSalespersons = useMemo(() => [...salespersons, ...localSalespersons], [salespersons, localSalespersons]);

	const shared = useGrowerAssignment({
		jobs,
		entities: allSalespersons,
		assignments: salespersonAssignments,
		onAssign: onAssignGrowerToSalesperson,
	});

	// Salesperson sort & search state
	const [spSortOption, setSpSortOption] = useState<EntitySortOption>("default");
	const [searchText, setSearchText] = useState("");

	const filteredSortedSalespersons = useMemo(() => {
		const trimmed = searchText.trim().toLowerCase();
		let result = allSalespersons;

		if (trimmed) {
			result = result.filter((sp) => sp.name.toLowerCase().includes(trimmed));
		}

		if (spSortOption === "default") return result;

		return [...result].sort((a, b) => {
			switch (spSortOption) {
				case "name-asc":
					return a.name.localeCompare(b.name);
				case "name-desc":
					return b.name.localeCompare(a.name);
				case "growers-desc": {
					const aCount = (shared.growersByEntity.get(a.id) || []).length;
					const bCount = (shared.growersByEntity.get(b.id) || []).length;
					return bCount - aCount;
				}
				case "growers-asc": {
					const aCount = (shared.growersByEntity.get(a.id) || []).length;
					const bCount = (shared.growersByEntity.get(b.id) || []).length;
					return aCount - bCount;
				}
				default:
					return 0;
			}
		});
	}, [allSalespersons, spSortOption, shared.growersByEntity, searchText]);

	// Validate phone number (optional field — only validate if non-empty)
	const validatePhone = useCallback((phone: string): string => {
		const trimmed = phone.trim();
		if (!trimmed) return "";
		if (!isValidPhoneNumber(trimmed, "US")) {
			return "Enter a valid US phone number";
		}
		return "";
	}, []);

	const handlePhoneChange = useCallback(
		(value: string) => {
			setNewPhone(value);
			if (phoneError) setPhoneError("");
		},
		[phoneError],
	);

	const handleAddNewSalesperson = useCallback(() => {
		const trimmedName = newName.trim();
		if (!trimmedName) return;

		const error = validatePhone(newPhone);
		if (error) {
			setPhoneError(error);
			return;
		}

		const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const firstName = trimmedName.split(" ")[0] || trimmedName;
		const lastName = trimmedName.split(" ").slice(1).join(" ") || "";
		const tempContact: Contact = {
			...newSalespersonContact(tempId, trimmedName, firstName, lastName),
			mobilePhone: newPhone.trim(),
		};

		setLocalSalespersons((prev) => [...prev, tempContact]);
		onAddSalesperson(trimmedName, newPhone.trim());

		setNewName("");
		setNewPhone("");
		setPhoneError("");
		setIsAddingNew(false);
	}, [newName, newPhone, onAddSalesperson, validatePhone]);

	const handleCancelAddNew = useCallback(() => {
		setNewName("");
		setNewPhone("");
		setPhoneError("");
		setIsAddingNew(false);
	}, []);

	const handleStartEdit = useCallback(
		(spId: string) => {
			const sp = localSalespersons.find((s) => s.id === spId);
			if (!sp) return;
			setEditingSpId(spId);
			setEditName(sp.name);
			setEditPhone(sp.mobilePhone || "");
			setEditPhoneError("");
		},
		[localSalespersons],
	);

	const handleEditPhoneChange = useCallback(
		(value: string) => {
			setEditPhone(value);
			if (editPhoneError) setEditPhoneError("");
		},
		[editPhoneError],
	);

	const handleSaveEdit = useCallback(() => {
		if (!editingSpId) return;
		const trimmedName = editName.trim();
		if (!trimmedName) return;

		const error = validatePhone(editPhone);
		if (error) {
			setEditPhoneError(error);
			return;
		}

		const trimmedPhone = editPhone.trim();
		const firstName = trimmedName.split(" ")[0] || trimmedName;
		const lastName = trimmedName.split(" ").slice(1).join(" ") || "";

		setLocalSalespersons((prev) =>
			prev.map((sp) => {
				if (sp.id !== editingSpId) return sp;
				return { ...sp, name: trimmedName, firstName, lastName, mobilePhone: trimmedPhone };
			}),
		);

		setEditingSpId(null);
		setEditName("");
		setEditPhone("");
		setEditPhoneError("");
	}, [editingSpId, editName, editPhone, validatePhone]);

	const handleCancelEdit = useCallback(() => {
		setEditingSpId(null);
		setEditName("");
		setEditPhone("");
		setEditPhoneError("");
	}, []);

	return {
		...shared,
		spSortOption,
		setSpSortOption,
		searchText,
		setSearchText,
		filteredSortedSalespersons,
		totalSalespersonCount: allSalespersons.length,
		// New salesperson creation
		isAddingNew,
		setIsAddingNew,
		newName,
		setNewName,
		newPhone,
		handlePhoneChange,
		phoneError,
		handleAddNewSalesperson,
		handleCancelAddNew,
		// Edit temp salesperson
		editingSpId,
		editName,
		setEditName,
		editPhone,
		handleEditPhoneChange,
		editPhoneError,
		handleStartEdit,
		handleSaveEdit,
		handleCancelEdit,
	};
}
